/**
 * useKeyboardShortcuts.js
 * Hook para gerenciamento de atalhos de teclado globais
 *
 * Features:
 * - Suporte a modificadores (Ctrl, Alt, Shift, Meta)
 * - Suporte a sequências (G + M, G + V)
 * - Ignora inputs e textareas (exceto Escape e Ctrl+K)
 * - Timeout para sequências (500ms)
 *
 * Uso:
 *   useKeyboardShortcuts({
 *     'ctrl+k': () => openCommandBar(),
 *     'g m': () => navigate('/map'),
 *     '?': () => showHelp(),
 *   });
 */

import { useEffect, useCallback, useRef } from 'react';

// Timeout para sequências de teclas (ms) - 1.5 segundos
const SEQUENCE_TIMEOUT = 1500;

/**
 * Constrói string normalizada do atalho
 */
const buildKeyString = (event) => {
  const parts = [];

  // Obter a tecla primeiro para verificar caracteres especiais
  // Proteção contra event.key undefined (teclas especiais ou eventos do browser)
  if (!event.key) return '';
  let key = event.key.toLowerCase();

  // Caracteres que são produzidos com Shift mas devem ser tratados como tecla única
  // (não incluir 'shift' no resultado para estes)
  const shiftProducedChars = ['?', '!', '@', '#', '$', '%', '^', '&', '*', '(', ')', '_', '+', '{', '}', '|', ':', '"', '<', '>', '~'];
  const isShiftProducedChar = shiftProducedChars.includes(event.key);

  // Adicionar modificadores (exceto shift para caracteres produzidos com shift)
  if (event.ctrlKey || event.metaKey) parts.push('ctrl');
  if (event.altKey) parts.push('alt');
  if (event.shiftKey && !isShiftProducedChar) parts.push('shift');

  // Mapear teclas especiais
  const keyMap = {
    'arrowup': 'up',
    'arrowdown': 'down',
    'arrowleft': 'left',
    'arrowright': 'right',
    ' ': 'space',
  };

  key = keyMap[key] || key;
  parts.push(key);

  return parts.join('+');
};

/**
 * Verifica se o evento está em um campo de input
 */
const isInputElement = (target) => {
  const tagName = target.tagName.toLowerCase();
  const isEditable = target.isContentEditable;
  return tagName === 'input' || tagName === 'textarea' || tagName === 'select' || isEditable;
};

/**
 * Hook principal para atalhos de teclado
 *
 * @param {Object} shortcuts - Mapa de atalhos { 'ctrl+k': callback, 'g m': callback }
 * @param {Object} options - Opções { enabled, preventDefault, enableInInputs }
 */
export const useKeyboardShortcuts = (shortcuts, options = {}) => {
  const {
    enabled = true,
    preventDefault = true,
    enableInInputs = ['escape', 'ctrl+k'], // Atalhos permitidos em inputs
  } = options;

  // Ref para sequência de teclas
  const sequenceRef = useRef({
    keys: [],
    timeout: null,
  });

  // Limpar sequência
  const clearSequence = useCallback(() => {
    if (sequenceRef.current.timeout) {
      clearTimeout(sequenceRef.current.timeout);
    }
    sequenceRef.current = { keys: [], timeout: null };
  }, []);

  // Adicionar tecla à sequência
  const addToSequence = useCallback((key) => {
    // Limpar apenas o timeout anterior, NÃO as teclas
    if (sequenceRef.current.timeout) {
      clearTimeout(sequenceRef.current.timeout);
    }
    // Adicionar tecla à sequência
    sequenceRef.current.keys.push(key);
    // Novo timeout para limpar a sequência
    sequenceRef.current.timeout = setTimeout(clearSequence, SEQUENCE_TIMEOUT);
    return sequenceRef.current.keys.join(' ');
  }, [clearSequence]);

  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (event) => {
      const target = event.target;
      const keyString = buildKeyString(event);

      // Verificar se está em input
      if (isInputElement(target)) {
        // Permitir apenas atalhos específicos em inputs
        const allowedInInput = enableInInputs.some((allowed) =>
          keyString === allowed || keyString.endsWith(allowed)
        );
        if (!allowedInInput) return;
      }

      // Ignorar teclas modificadoras sozinhas ou eventos sem key
      if (!event.key || ['control', 'alt', 'shift', 'meta'].includes(event.key.toLowerCase())) {
        return;
      }

      // Verificar atalho direto
      if (shortcuts[keyString]) {
        if (preventDefault) event.preventDefault();
        shortcuts[keyString](event);
        clearSequence();
        return;
      }

      // Verificar sequência (ex: 'g m')
      // Só considerar sequência se for tecla simples (sem modificadores)
      if (!event.ctrlKey && !event.altKey && !event.metaKey) {
        const sequenceString = addToSequence(keyString);

        if (shortcuts[sequenceString]) {
          if (preventDefault) event.preventDefault();
          shortcuts[sequenceString](event);
          clearSequence();
          return;
        }

        // Verificar se a sequência ainda é válida (prefixo de algum atalho)
        const isValidPrefix = Object.keys(shortcuts).some((shortcut) =>
          shortcut.startsWith(sequenceString + ' ')
        );

        if (!isValidPrefix) {
          clearSequence();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      clearSequence();
    };
  }, [shortcuts, enabled, preventDefault, enableInInputs, addToSequence, clearSequence]);
};

/**
 * Hook para registrar um único atalho
 */
export const useKeyboardShortcut = (key, callback, options = {}) => {
  const shortcuts = { [key]: callback };
  useKeyboardShortcuts(shortcuts, options);
};

/**
 * Formata atalho para exibição
 * 'ctrl+k' -> ['⌘', 'K'] ou ['Ctrl', 'K']
 */
export const formatShortcut = (shortcut, isMac = null) => {
  if (isMac === null) {
    isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
  }

  return shortcut.split(' ').map((part) => {
    return part.split('+').map((key) => {
      const keyMap = {
        ctrl: isMac ? '⌘' : 'Ctrl',
        alt: isMac ? '⌥' : 'Alt',
        shift: '⇧',
        meta: isMac ? '⌘' : 'Win',
        enter: '↵',
        escape: 'Esc',
        space: 'Space',
        up: '↑',
        down: '↓',
        left: '←',
        right: '→',
        backspace: '⌫',
        delete: 'Del',
        tab: '⇥',
      };
      return keyMap[key.toLowerCase()] || key.toUpperCase();
    });
  });
};

export default useKeyboardShortcuts;
